import { useEffect, useSyncExternalStore } from 'react';
import { teamStore } from '../lib/teamStore';
import { useAppMode } from '../contexts/AppModeContext';
import { DEMO_TEAM_MEMBERS } from '../lib/fixtures/teamMembersDemo';

/**
 * useTeamMembers — React hook that subscribes to the singleton teamStore.
 *
 * Guarantees every tab ({`Settings → Team`, `Settings → Roles`, ...}) sees
 * identical member data and mutations propagate instantly across tabs.
 */
export function useTeamMembers() {
  const { isDemo } = useAppMode();
  const snap = useSyncExternalStore(
    teamStore.subscribe,
    teamStore.getSnapshot,
    teamStore.getSnapshot,
  );

  // Sync mode on mode flip; hydrate automatically.
  useEffect(() => {
    if (isDemo) {
      teamStore.setMode('demo');
      teamStore.seedDemo(DEMO_TEAM_MEMBERS);
    } else {
      teamStore.setMode('real');
      teamStore.fetch();
    }
  }, [isDemo]);

  return {
    members: snap.members,
    loading: snap.loading,
    error: snap.error,
    hydrated: snap.hydrated,
    refresh: () => teamStore.fetch(true),
    setRole: teamStore.setRole,
    setSchedulable: teamStore.setSchedulable,
    linkNpi: teamStore.linkNpi,
    unlinkNpi: teamStore.unlinkNpi,
    updateMember: teamStore.updateMember,
    createMember: teamStore.createMember,
    deleteMember: teamStore.deleteMember,
  };
}

export default useTeamMembers;
